import { faChevronDown, faChevronRight, faSquare, faSquareCheck } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography
} from "@mui/material"
import { Box } from "@mui/system"
import { translate } from "app/language/service"
import { OTHER_ENVIRONMENT_GROUP_ID } from "environments/model"
import { getAllEnvironmentChildrenIds, getEnvironmentTree } from "environments/tools/common"
import { IForm, IFormEnvironmentsRow } from "form/model"
import {
  getEnvironmentTreeInfo,
  noticeAndClearEnvironmentsError,
  synchronizeNewEnvironmentsSelection,
  toggleEnvironmentTreeElement
} from "form/tools/environments"
import { getTreeEnvironmentRows } from "form/tools/environments-forecast"
import { messageSVC } from "message/service"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { ColumnHeader } from "../../../../ui/components/ColumnHeader"
import { ColorizedText } from "./ColorizedText"

interface IEnvironmentsTreeProps {
  form: IForm
}

interface IColumnDefinition {
  renderHeader: () => JSX.Element
  renderCell: (row: IFormEnvironmentsRow) => JSX.Element
}

export const EnvironmentsTree = React.memo(({ form }: IEnvironmentsTreeProps): JSX.Element => {
  const isNoticingError = React.useRef(false)
  const [selectedIds, setSelectedIds] = React.useState<number[]>(form.screens.environmentsIds)
  React.useEffect(() => {
    if (form.context.relations.environmentsError) {
      if (!isNoticingError.current) {
        isNoticingError.current = true
        noticeAndClearEnvironmentsError(form)
        setSelectedIds(form.screens.environmentsIds)
      }
    } else if (isNoticingError.current) {
      isNoticingError.current = false
    }
  }, [form])
  const rows = getTreeEnvironmentRows(form, getEnvironmentTree())
  const isCreateForm = form.context.isCreation
  const expandedIds = form.screens.environmentExpandedIds
  const columns: IColumnDefinition[] = [
    {
      renderHeader: () => <ColumnHeader primaryText={translate("label.name")} />,
      renderCell: row => {
        const checkboxInfo = getEnvironmentTreeInfo(expandedIds, selectedIds, row.id)
        const hasChildren = row.environment.children.length > 0
        return (
          <FlexBox
            jc="start"
            sx={{
              pl: `${row.environment.treeDepth}rem`,
              maxWidth: "100%"
            }}>
            {hasChildren ? (
              <FontAwesomeIcon
                id={`environment-tree-${row.environment.id}-expand-button`}
                icon={expandedIds.includes(row.id) ? faChevronDown : faChevronRight}
                className="icon"
                onClick={event => {
                  event.stopPropagation()
                  toggleEnvironmentTreeElement(form, row.id)
                }}
              />
            ) : (
              <span className="wad" />
            )}
            {!row.isIndeterminate && checkboxInfo.isDisabled && checkboxInfo.isTargeted ? (
              <span className="targeted-icon-disabled-span">
                <FontAwesomeIcon
                  id={`environment-tree-${row.environment.id}-disabled-and-targeted`}
                  icon={faSquareCheck}
                  className="targeted-icon-disabled"
                  onClick={() => {
                    if (row.id === OTHER_ENVIRONMENT_GROUP_ID) {
                      messageSVC.info(translate("campaign.environment.selection.other.forbidden", row.name))
                      return
                    }
                    messageSVC.info(translate("campaign.environment.selection.not.usefull", row.name))
                  }}
                />
              </span>
            ) : (
              <Checkbox
                id={`environment-tree-${row.environment.id}-select-checkbox-input`}
                itemID={`environment-tree-${row.environment.id}-select-checkbox`}
                checked={selectedIds.includes(row.id)}
                indeterminate={row.isIndeterminate}
                className={
                  selectedIds.includes(row.id)
                    ? "tree-selected"
                    : !checkboxInfo.isSelected && checkboxInfo.isTargeted
                      ? "not-selected-but-targeted"
                      : "not-selected"
                }
                icon={
                  !checkboxInfo.isSelected && checkboxInfo.isTargeted ? (
                    <FontAwesomeIcon
                      id={`environment-tree-${row.environment.id}-select-checkbox-icon`}
                      icon={faSquare}
                      className="targeted-icon"
                    />
                  ) : undefined
                }
                onChange={(_event, checked) => {
                  let newSelectedIds: number[]
                  if (checked) {
                    // when selecting a parent, we unselect all of its children
                    const childrenIds = getAllEnvironmentChildrenIds(row.environment)
                    newSelectedIds = [...selectedIds.filter(id => !childrenIds.includes(id)), row.id]
                  } else {
                    newSelectedIds = selectedIds.filter(id => id !== row.id)
                  }
                  synchronizeNewEnvironmentsSelection(newSelectedIds)
                  setSelectedIds(newSelectedIds)
                }}
                disabled={checkboxInfo.isDisabled}
              />
            )}
            <Tooltip title={row.name}>
              <Typography
                id={`environment-tree-${row.environment.id}-name`}
                color="textPrimary"
                fontSize=".8rem"
                sx={{
                  cursor: hasChildren ? "pointer" : "default"
                }}
                onClick={event => {
                  event.stopPropagation()
                  if (hasChildren) {
                    toggleEnvironmentTreeElement(form, row.id)
                  }
                }}>
                {row.name}
              </Typography>
            </Tooltip>
          </FlexBox>
        )
      }
    },
    {
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.impression.ratio.primary")}
          secondaryText={translate("label.impression.ratio.secondary")}
        />
      ),
      renderCell: row => (
        <ColorizedText
          id={`environment-tree-${row.environment.id}-impression-ratio`}
          text={row.impressionRatio}
          difference={row.impressionDiff}
          isLoading={form.screens.isLoadingEnvironmentsForecast}
          disabled={row.impression === 0}
          isCreationForm={isCreateForm}
        />
      )
    },
    {
      renderHeader: () => <ColumnHeader primaryText={translate("label.impression.percent")} />,
      renderCell: row => (
        <ColorizedText
          id={`environment-tree-${row.environment.id}-impression-percent`}
          text={row.impressionPercent === -1 ? "-" : `${row.impressionPercent}%`}
          difference={row.impressionDiff}
          isLoading={form.screens.isLoadingEnvironmentsForecast}
          disabled={row.impression === 0}
          isCreationForm={isCreateForm}
        />
      )
    },
    {
      renderHeader: () => (
        <ColumnHeader
          primaryText={translate("label.screen.ratio.primary")}
          secondaryText={translate("label.screen.ratio.secondary")}
        />
      ),
      renderCell: row => (
        <ColorizedText
          id={`environment-tree-${row.environment.id}-screen-ratio`}
          text={row.screensRatio}
          difference={row.screensDiff}
          isLoading={form.screens.isLoadingEnvironmentsForecast}
          disabled={row.screens === 0}
          isCreationForm={isCreateForm}
        />
      )
    },
    {
      renderHeader: () => <ColumnHeader primaryText={translate("label.screen.percent")} />,
      renderCell: row => (
        <ColorizedText
          id={`environment-tree-${row.environment.id}-screen-percent`}
          text={row.screensPercent === -1 ? "-" : `${row.screensPercent}%`}
          difference={row.screensDiff}
          isLoading={form.screens.isLoadingEnvironmentsForecast}
          disabled={row.screens === 0}
          isCreationForm={isCreateForm}
        />
      )
    }
  ]
  return (
    <Box
      sx={{
        "mt": "1rem",
        "& > *": {
          width: "100%"
        },
        "& .icon": {
          cursor: "pointer",
          p: ".4rem",
          pr: 0,
          width: ".9rem",
          minWidth: ".9rem",
          maxWidth: ".9rem",
          height: ".9rem"
        },
        "& .wad": {
          width: "1.3rem",
          minWidth: "1.3rem",
          maxWidth: "1.3rem"
        },
        "& tbody tr > th": {
          p: 0,
          pl: "1rem",
          pr: "1rem"
        }
      }}>
      <TableContainer>
        <Table
          id="environment-tree"
          sx={{
            "border": theme => theme.palette.border,
            "borderRadius": ".125rem",
            "minWidth": 650,
            "& .targeted-icon": {
              color: theme => theme.palette.text.primary2,
              fontSize: "1.3rem",
              p: "0.1rem",
              pl: "0.17rem",
              pr: "0.17rem"
            },
            "& .targeted-icon-disabled": {
              color: theme => theme.palette.text.disabled,
              fontSize: "1.3rem",
              p: "0.1rem",
              pl: "0.17rem",
              pr: "0.17rem"
            },
            "& .targeted-icon-disabled-span": {
              p: ".56rem",
              pt: ".45rem",
              pb: ".49rem"
            }
          }}
          size="small"
          aria-label="simple table">
          <TableHead>
            <TableRow>
              {columns.map((c, index) => (
                <TableCell key={index}>{c.renderHeader()}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <Typography color="textSecondary" sx={{ m: "1rem" }}>
                {translate("campaign.environment.not.available")}
              </Typography>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow
                  id={`environment-tree-${rowIndex}`}
                  key={rowIndex}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 }
                  }}>
                  {columns.map((c, columnIndex) => (
                    <TableCell
                      id={`environment-tree-${rowIndex}-${columnIndex}`}
                      component="th"
                      scope="row"
                      key={columnIndex}>
                      {c.renderCell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
})
